import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bootstrapSupplierQuotation,
  createSupplierQuotation,
  createSupplierQuotationItem,
  deleteSupplierQuotationItem,
  getActiveSupplierQuotation,
  getSupplierQuotation,
  getTodaySupplierQuotation,
  listSupplierQuotations,
  listSupplierQuotationSubmissions,
  listSupplierQuotationItems,
  setActiveSupplierQuotation,
  updateSupplierQuotation,
  updateSupplierQuotationItem,
} from "./api";
import type {
  BootstrapSupplierQuotationPayload,
  CreateSupplierQuotationItemPayload,
  CreateSupplierQuotationPayload,
  UpdateSupplierQuotationPayload,
  UpdateSupplierQuotationItemPayload,
} from "./types";

const ACTIVE_SUPPLIER_QUOTATION_KEY = ["supplier-quotation", "active"];
const TODAY_SUPPLIER_QUOTATION_KEY = ["supplier-quotation", "today"];
const SUPPLIER_QUOTATIONS_KEY = ["supplier-quotation", "list"];
const supplierQuotationKey = (quotationId: string) => [
  "supplier-quotation",
  quotationId,
];
const supplierQuotationSubmissionsKey = (date: string) => [
  "supplier-quotation",
  "submissions",
  date,
];
const supplierQuotationItemsKey = (quotationId: string) => [
  "supplier-quotation",
  quotationId,
  "items",
];

export function useTodaySupplierQuotation() {
  return useQuery({
    queryKey: TODAY_SUPPLIER_QUOTATION_KEY,
    queryFn: getTodaySupplierQuotation,
    retry: false,
  });
}

export function useActiveSupplierQuotation() {
  return useQuery({
    queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY,
    queryFn: getActiveSupplierQuotation,
    retry: false,
  });
}

export function useSupplierQuotations() {
  return useQuery({
    queryKey: SUPPLIER_QUOTATIONS_KEY,
    queryFn: () => listSupplierQuotations(2000, 0),
  });
}

export function useSupplierQuotation(quotationId: string) {
  return useQuery({
    queryKey: supplierQuotationKey(quotationId),
    queryFn: () => getSupplierQuotation(quotationId),
    enabled: !!quotationId,
  });
}

export function useCreateSupplierQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierQuotationPayload) =>
      createSupplierQuotation(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: TODAY_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: SUPPLIER_QUOTATIONS_KEY });
    },
  });
}

export function useBootstrapSupplierQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      payload,
    }: {
      sourceId: string;
      payload: BootstrapSupplierQuotationPayload;
    }) => bootstrapSupplierQuotation(sourceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: TODAY_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: SUPPLIER_QUOTATIONS_KEY });
    },
  });
}

export function useUpdateSupplierQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      quotationId,
      payload,
    }: {
      quotationId: string;
      payload: UpdateSupplierQuotationPayload;
    }) => updateSupplierQuotation(quotationId, payload),
    onSuccess: (_, { quotationId }) => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: TODAY_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: SUPPLIER_QUOTATIONS_KEY });
      qc.invalidateQueries({ queryKey: supplierQuotationKey(quotationId) });
    },
  });
}

export function useSetActiveSupplierQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quotationId: string | null) =>
      setActiveSupplierQuotation(quotationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: SUPPLIER_QUOTATIONS_KEY });
    },
  });
}

export function useSupplierQuotationSubmissions(date: string) {
  return useQuery({
    queryKey: supplierQuotationSubmissionsKey(date),
    queryFn: () => listSupplierQuotationSubmissions(date, 2000, 0),
    enabled: !!date,
  });
}

export function useSupplierQuotationItems(quotationId: string) {
  return useQuery({
    queryKey: supplierQuotationItemsKey(quotationId),
    queryFn: () => listSupplierQuotationItems(quotationId, 2000, 0),
    enabled: !!quotationId,
  });
}

export function useCreateSupplierQuotationItem(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierQuotationItemPayload) =>
      createSupplierQuotationItem(quotationId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: TODAY_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: supplierQuotationKey(quotationId) });
      qc.invalidateQueries({
        queryKey: supplierQuotationItemsKey(quotationId),
      });
    },
  });
}

export function useUpdateSupplierQuotationItem(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      payload,
    }: {
      itemId: string;
      payload: UpdateSupplierQuotationItemPayload;
    }) => updateSupplierQuotationItem(quotationId, itemId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: TODAY_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: supplierQuotationKey(quotationId) });
      qc.invalidateQueries({
        queryKey: supplierQuotationItemsKey(quotationId),
      });
    },
  });
}

export function useDeleteSupplierQuotationItem(quotationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      deleteSupplierQuotationItem(quotationId, itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ACTIVE_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: TODAY_SUPPLIER_QUOTATION_KEY });
      qc.invalidateQueries({ queryKey: supplierQuotationKey(quotationId) });
      qc.invalidateQueries({
        queryKey: supplierQuotationItemsKey(quotationId),
      });
    },
  });
}
